"use client";

import { cn } from "@v1/ui/utils";
import { BookOpen, House, type LucideIcon, MessageCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactElement } from "react";
import { useMessenger } from "./messenger-context";
import type { MessengerTab } from "./messenger-types";

interface TabDef {
  readonly tab: MessengerTab;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly dot: boolean;
}

/** Bottom tabs: Start, Nachrichten (signed-in only), Hilfe. */
export function MessengerTabBar({ active }: { readonly active: MessengerTab }): ReactElement {
  const t = useTranslations("messenger.tabs");
  const { audience, nav, unread } = useMessenger();
  const member = audience.kind === "member";
  const tabs: TabDef[] = [
    { tab: "home", icon: House, label: t("start"), dot: false },
    ...(member ? [{ tab: "messages" as const, icon: MessageCircle, label: t("nachrichten"), dot: unread.length > 0 }] : []),
    { tab: "help", icon: BookOpen, label: t("hilfe"), dot: false },
  ];
  return (
    <nav className="shrink-0 border-t border-border/40 bg-background pb-[env(safe-area-inset-bottom,0px)]">
      <ul className="flex h-16">
        {tabs.map(({ tab, icon: Icon, label, dot }) => {
          const isActive = tab === active;
          return (
            <li key={tab} className="flex flex-1">
              <button
                type="button"
                onClick={() => nav.selectTab(tab)}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors",
                  isActive ? "text-action-700" : "text-primary/45 hover:text-primary/70",
                )}
              >
                <span className="relative">
                  <Icon className="h-5 w-5" strokeWidth={isActive ? 2.2 : 1.8} aria-hidden />
                  {dot ? (
                    <span
                      aria-hidden
                      className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-action-500 ring-2 ring-background"
                    />
                  ) : null}
                </span>
                {label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
